import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CableDialogModel, CableLocation } from './cable-dialog.model';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { catchError, first, map, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import { CompanyDto } from '@app/generated/model';
import { WarehouseLocationControllerService } from '@app/generated/service';
import { UserService } from '@app/core/services/user.service';
import { CompanySelectDialogComponent } from '@app/shared/company-select-dialog/company-select-dialog.component';

interface CableDialogData {
  model: CableDialogModel;
  projectId: string;
}

@Component({
  selector: 'app-cable-dialog',
  templateUrl: './cable-dialog.component.html',
  styleUrls: ['./cable-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CableDialogComponent implements OnDestroy {
  public model: CableDialogModel;
  public locations: CableLocation[] = [];
  public locationsLoading = true;
  public cableForm: UntypedFormGroup;
  public company?: CompanyDto;
  private readonly projectId: string;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: CableDialogData,
    private readonly matDialog: MatDialog,
    private readonly userService: UserService,
    private readonly warehouseLocationController: WarehouseLocationControllerService,
  ) {
    this.model = data.model;
    this.projectId = (data.projectId ?? '').toString();
    this.cableForm = new UntypedFormGroup({
      whole: new UntypedFormControl(this.splitDouble(this.model.installedLength)[0], [
        Validators.min(0),
        Validators.pattern(/^\d+$/),
        Validators.required,
      ]),
      decimal: new UntypedFormControl(this.splitDouble(this.model.installedLength)[1], [
        Validators.min(0),
        Validators.max(99),
        Validators.pattern(/^[0-9]{0,2}$/),
      ]),
      locationExternalId: new UntypedFormControl(this.model.locationExternalId ?? this.model.selectedLocationId ?? '', [
        Validators.required,
        CableDialogComponent.integerValidator,
      ]),
    });

    this.setInitialValues();

    this.company = this.resolveCompanyForProject();
    if (this.hasCompany()) {
      this.loadLocations();
    } else {
      // First time for this project: no company selected yet, so prompt for one.
      this.locationsLoading = false;
      this.openCompanySelector();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  hasCompany(): boolean {
    return !!this.company?.companyId;
  }

  getCompanyName(): string {
    return this.company?.companyName || this.company?.companyNo || this.company?.id || '';
  }

  editCompany(): void {
    this.openCompanySelector();
  }

  private openCompanySelector(): void {
    this.matDialog
      .open(CompanySelectDialogComponent, {
        data: {
          projectId: this.projectId,
          selectedCompanyId: this.company?.id,
        },
      })
      .afterClosed()
      .pipe(first(), takeUntil(this.destroyed$))
      .subscribe((company: CompanyDto | null) => {
        if (!company) {
          return;
        }
        this.company = company;
        this.loadLocations();
      });
  }

  private resolveCompanyForProject(): CompanyDto | undefined {
    const user = this.userService.get();
    if (!user || !this.projectId) {
      return undefined;
    }

    const projects = user.projects ?? [];
    const exactMatches = projects.filter((p) => (p.id ?? '').toString() === this.projectId);
    if (exactMatches.length === 1) {
      return exactMatches[0].company;
    }

    const normalizedProjectId = this.projectId.trim();
    const normalizedMatches = projects.filter((p) => (p.id ?? '').toString().trim() === normalizedProjectId);
    if (normalizedMatches.length === 1) {
      return normalizedMatches[0].company;
    }

    return undefined;
  }

  private loadLocations(): void {
    const companyId = this.company?.companyId;
    const articleId = this.model.material?.externalId;
    if (!companyId || !articleId) {
      this.locations = [];
      this.locationsLoading = false;
      return;
    }

    this.locationsLoading = true;
    this.warehouseLocationController
      .getWarehouseLocations(companyId, articleId, this.projectId.trim())
      .pipe(
        map((dtos) =>
          dtos.map((dto) => ({
            id: String(dto.locationId),
            name: dto.locationName ?? '',
            number: dto.locationNo,
            quantity: dto.onStockQty,
          })),
        ),
        catchError(() => of([] as CableLocation[])),
        first(),
        takeUntil(this.destroyed$),
      )
      .subscribe((locations) => {
        this.locations = locations;
        this.locationsLoading = false;
      });
  }

  canSubmit(): boolean {
    if (this.locationsLoading) {
      return true;
    }

    if (this.isArticleMissing()) {
      return true;
    }

    const wholeHasErrors =
      this.getWhole().hasError('max') ||
      this.getWhole().hasError('min') ||
      this.getWhole().hasError('pattern') ||
      this.getWhole().hasError('required');

    const decimalHasErrors =
      this.getDecimal().hasError('max') || this.getDecimal().hasError('min') || this.getDecimal().hasError('pattern');

    const locationHasErrors =
      this.getLocationExternalId().hasError('required') || this.getLocationExternalId().hasError('integer');

    return wholeHasErrors || decimalHasErrors || locationHasErrors || this.exceedsAvailableQuantity();
  }

  isArticleMissing(): boolean {
    return !this.model.material?.externalId;
  }

  getWhole() {
    return this.cableForm.get('whole')!;
  }

  setWhole(value: number | string) {
    this.getWhole().setValue(typeof value === 'string' ? value : String(value));
  }

  getDecimal() {
    return this.cableForm.get('decimal')!;
  }

  setDecimal(value: number | string) {
    this.getDecimal().setValue(typeof value === 'string' ? value : String(value));
  }

  getLocationExternalId() {
    return this.cableForm.get('locationExternalId')!;
  }

  private static integerValidator(control: AbstractControl): ValidationErrors | null {
    const rawValue = (control.value ?? '').toString().trim();
    if (!rawValue) {
      return null;
    }

    return /^\d+$/.test(rawValue) ? null : { integer: true };
  }

  splitDouble(value: number | null): number[] {
    return value !== null && value !== undefined
      ? String(value)
          .split(/[,.]/)
          .map((v) => Number(v))
      : [0, 0];
  }

  combineToDouble(whole: number, decimal: number): number {
    return Number(`${whole}.${decimal ? decimal : 0}`);
  }

  setInitialValues() {
    const breakdown = this.splitDouble(this.model.installedLength);
    this.setWhole(breakdown[0]);
    this.setDecimal(breakdown[1] ? breakdown[1] : '00');
  }

  increaseValueBy(increase: number) {
    const whole = Number(this.getWhole().value);
    this.setWhole(whole + increase);
  }

  decreaseValueBy(decrease: number) {
    const whole = Number(this.getWhole().value);
    this.setWhole(Math.max(0, whole - decrease));
  }

  showStepButton(step: number): boolean {
    return Math.abs(step) <= this.model.plannedLength;
  }

  setValuesToPlanned() {
    const breakdown = this.splitDouble(this.model.plannedLength);
    this.setWhole(breakdown[0]);
    this.setDecimal(breakdown[1] ? breakdown[1] : '00');
  }

  confirm(): void {
    this.model.installedLength = this.combineToDouble(Number(this.getWhole().value), Number(this.getDecimal().value));
    const locationExternalId = (this.getLocationExternalId().value || '').toString().trim();
    this.model.locationExternalId = locationExternalId || undefined;
    this.model.selectedLocationId = this.model.locationExternalId;
    this.dialogRef.close(this.model);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  hasLocations(): boolean {
    return this.locations.length > 0;
  }

  getSelectedLocation(): CableLocation | undefined {
    const selectedId = (this.getLocationExternalId().value ?? '').toString();
    return this.locations.find((location) => location.id === selectedId);
  }

  getPulledLength(): number {
    return this.combineToDouble(Number(this.getWhole().value), Number(this.getDecimal().value));
  }

  exceedsAvailableQuantity(): boolean {
    const location = this.getSelectedLocation();
    if (!location || location.quantity == null) {
      return false;
    }
    return this.getPulledLength() > location.quantity;
  }
}
